import { useState } from "react";
import { useQuery } from "react-query";
import { apiService } from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import Pagination from "../components/Pagination";

interface SummaryRow {
  costType: string;
  costCenter: string;
  cost: number;
  count: number;
}

export default function BillingSummaryPage() {
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [costType, setCostType] = useState("");
  const [costCenterFilter, setCostCenterFilter] = useState("");

  const { data, isLoading } = useQuery(
    ["billing-summary", month],
    () => apiService.getInvoiceSummary({ month: month || undefined }),
    { keepPreviousData: true },
  );

  const rows: SummaryRow[] = data?.data?.data?.rows ?? [];
  const filteredRows = rows.filter((r) => {
    if (costType && r.costType !== costType) return false;
    if (
      costCenterFilter &&
      !r.costCenter.toLowerCase().includes(costCenterFilter.toLowerCase())
    )
      return false;
    return true;
  });
  const totalPages = Math.ceil(filteredRows.length / itemsPerPage);
  const paginatedRows = filteredRows.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Summary
        </h1>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {filteredRows.length} lines &middot; Total:{" "}
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            $
            {filteredRows
              .reduce((sum, r) => sum + r.cost, 0)
              .toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
          </span>
        </div>
      </div>

      <p className="text-sm text-gray-500 dark:text-gray-400">
        This page shows your own Claude usage and licence lines, plus the data
        for any cost centers you approve/own.
      </p>

      <div className="card p-4">
        <div className="flex gap-4">
          <div className="max-w-xs">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Month
            </label>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="input w-full"
            />
          </div>
          <div className="max-w-xs">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Cost Type
            </label>
            <select
              value={costType}
              onChange={(e) => {
                setCostType(e.target.value);
                setCurrentPage(1);
              }}
              className="input w-full"
            >
              <option value="">All</option>
              <option value="Repository Cost">Repository Cost</option>
              <option value="Copilot Usage">Copilot Usage</option>
              <option value="Claude Usage">Claude Usage</option>
              <option value="Claude Licence">Claude Licence</option>
            </select>
          </div>
          <div className="max-w-xs">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Cost Center
            </label>
            <input
              type="text"
              value={costCenterFilter}
              onChange={(e) => {
                setCostCenterFilter(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Filter..."
              className="input w-full"
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Cost Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Cost Center
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Count
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Cost
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                    >
                      No data found
                    </td>
                  </tr>
                ) : (
                  paginatedRows.map((row, idx) => (
                    <tr
                      key={idx}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <CostTypeBadge costType={row.costType} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                        {row.costCenter || "—"}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-900 dark:text-gray-100">
                        {row.count}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900 dark:text-gray-100">
                        $
                        {row.cost.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {rows.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredRows.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(n) => {
                setItemsPerPage(n);
                setCurrentPage(1);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

function CostTypeBadge({ costType }: { costType: string }) {
  const colors: Record<string, string> = {
    "Repository Cost":
      "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
    "Copilot Usage":
      "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
    "Claude Usage":
      "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
    "Claude Licence":
      "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  };

  const colorClass =
    colors[costType] ??
    "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${colorClass}`}
    >
      {costType}
    </span>
  );
}
